import EPIssueButton from 'editor-components/header/EPIssueButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import EditorApprovalButtons from '../approvals/EditorApprovalButtons';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUnifiedPublishType } from '../../redux/selectors/publishSelectors';
import UILockBadge from 'UIComponents/badge/UILockBadge';
// @ts-expect-error Untyped module
import { updatePublishImmediately } from '../../redux/actions/publishActions';
import { PublishTypes } from '../../constants/PublishTypes';
import { PublishActionModalNames } from '../publishing/types/publishButtonTypes';
import useEditorPublishProps from '../publishing/hooks/useEditorPublishProps';
import EditorPublishButtonActionModals from '../publishing/modals/EditorPublishButtonActionModals';
import useContentCrmObject from '../publishing/hooks/useContentCrmObject';
import { CONTENT_PLATFORM_PROPERTY_NAME, getContentPropertyValue } from 'content-management-data-lib';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const EditorPublishButton = ({
  cannotPublishOnDomainReason,
  errors,
  warnings,
  canSchedule,
  publishEnabled,
  unpublishEnabled,
  testVariationTooltipPropsForApproval = {
    isTestVariation: false,
    testVariationDisabledTooltip: undefined
  },
  extraDropdownActions,
  shouldOverrideDefaultButtonTextKey = false,
  buttonTextKey,
  updateFocusedItemAction,
  tooltipOverride,
  lockedPublishButtonProps,
  customPublishActionProps = {},
  publish,
  unpublish,
  schedulePublishUnpublish,
  isNotAuthorizedToPublish = false,
  confirmPublishActionProps,
  schedulingOptionsModalProps = {}
}) => {
  const {
    publishDropdownActions,
    approvalDropdownActions,
    isPublishEnabled,
    isPublishInProgress,
    loadingFailed,
    publishErrors,
    publishWarnings,
    errorsAndWarningsCount,
    onIssueClick,
    handleMouseEnterButton,
    handleTrackOnDropdownCompleteAction,
    openModal,
    closeModal,
    openModalName,
    cancelScheduleModalType,
    setCancelScheduleModalType
  } = useEditorPublishProps({
    canSchedule,
    cannotPublishOnDomainReason,
    lockedPublishButtonProps,
    extraDropdownActions,
    unpublishEnabled,
    publishEnabled,
    errors,
    warnings,
    updateFocusedItemAction
  });
  const {
    contentCrmObjectPublishType,
    canScheduleWithContentCrmObject,
    isContentCrmObjectLoading,
    contentCrmObject
  } = useContentCrmObject();
  const unifiedPublishType = useSelector(getUnifiedPublishType);
  const finalButtonTextKey = useMemo(() => {
    if (canScheduleWithContentCrmObject && contentCrmObjectPublishType && !shouldOverrideDefaultButtonTextKey // this is required for overriding the publish button text in staged pages editor
    ) {
      if (isNotAuthorizedToPublish) {
        return `header.publishButton.notAuthorized.label`;
      }
      return `header.publishButton.${contentCrmObjectPublishType}.label`;
    }
    return buttonTextKey;
  }, [buttonTextKey, canScheduleWithContentCrmObject, contentCrmObjectPublishType, isNotAuthorizedToPublish, shouldOverrideDefaultButtonTextKey]);
  const dispatch = useDispatch();
  const handlePublishButtonClick = useCallback(() => {
    if (lockedPublishButtonProps !== null && lockedPublishButtonProps !== void 0 && lockedPublishButtonProps.isLocked && lockedPublishButtonProps !== null && lockedPublishButtonProps !== void 0 && lockedPublishButtonProps.onClickLocked) {
      lockedPublishButtonProps.onClickLocked();
      return;
    }
    if (customPublishActionProps !== null && customPublishActionProps !== void 0 && customPublishActionProps.shouldUseCustomPublishHandler && customPublishActionProps !== null && customPublishActionProps !== void 0 && customPublishActionProps.customPublishHandler) {
      customPublishActionProps.customPublishHandler();
      return;
    }
    if (!contentCrmObject && unifiedPublishType !== PublishTypes.schedule) {
      dispatch(updatePublishImmediately(true));
    }
    if (confirmPublishActionProps !== null && confirmPublishActionProps !== void 0 && confirmPublishActionProps.shouldConfirmPublishAction) {
      openModal(PublishActionModalNames.PUBLISH_CONFIRM);
      return;
    }
    if (canScheduleWithContentCrmObject && contentCrmObjectPublishType === PublishTypes.schedule && schedulePublishUnpublish && contentCrmObject) {
      schedulePublishUnpublish({
        schedulePublishDate: getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE)
      });
      return;
    }
    publish(contentCrmObjectPublishType || unifiedPublishType);
  }, [canScheduleWithContentCrmObject, confirmPublishActionProps === null || confirmPublishActionProps === void 0 ? void 0 : confirmPublishActionProps.shouldConfirmPublishAction, contentCrmObject, contentCrmObjectPublishType, customPublishActionProps, dispatch, lockedPublishButtonProps, openModal, publish, schedulePublishUnpublish, unifiedPublishType]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(EditorApprovalButtons, Object.assign({
      errorsAndWarningsCount: errorsAndWarningsCount,
      onOpenWarningsAndSuggestionsPanel: () => openModal(PublishActionModalNames.WARNINGS_AND_SUGGESTIONS),
      buttonSize: "sm",
      extraActionButtons: approvalDropdownActions
    }, testVariationTooltipPropsForApproval, {
      children: /*#__PURE__*/_jsx("div", {
        "data-onboarding": "onboarding-publish-button",
        "data-campaign-assistant-onboarding-tour": "tour-step-publish",
        children: /*#__PURE__*/_jsxs(EPIssueButton, {
          actions: publishDropdownActions,
          "data-test": "publish-issue-button",
          errors: publishErrors,
          warnings: publishWarnings,
          tooltipOverride: tooltipOverride,
          onButtonClick: handlePublishButtonClick,
          onDropdownOpenComplete: handleTrackOnDropdownCompleteAction,
          onIssueClick: onIssueClick,
          onMouseEnter: handleMouseEnterButton,
          disabled: !isPublishEnabled || isContentCrmObjectLoading,
          loading: isPublishInProgress,
          loadingFailed: loadingFailed,
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: finalButtonTextKey
          }), lockedPublishButtonProps !== null && lockedPublishButtonProps !== void 0 && lockedPublishButtonProps.isLocked ? /*#__PURE__*/_jsx(UILockBadge, {}) : null]
        })
      })
    })), /*#__PURE__*/_jsx(EditorPublishButtonActionModals, {
      publishErrors: publishErrors,
      publishWarnings: publishWarnings,
      buttonTextKey: finalButtonTextKey,
      onIssueClick: onIssueClick,
      publish: publish,
      unpublish: unpublish,
      openModal: openModal,
      closeModal: closeModal,
      schedulePublishUnpublish: schedulePublishUnpublish,
      confirmPublishActionProps: confirmPublishActionProps,
      schedulingOptionsModalProps: schedulingOptionsModalProps,
      openModalName: openModalName,
      cancelScheduleModalType: cancelScheduleModalType,
      setCancelScheduleModalType: setCancelScheduleModalType
    })]
  });
};
export default EditorPublishButton;